/**
 * Публичная ссылка отчёта: `/r/:slug`.
 *
 * API выдаёт `share_url` вида `{base}/r/{slug}`, кабинет рядом с кнопкой
 * обещает «Кто угодно с этой ссылкой увидит числа за период» — а маршрута не
 * было вовсе: гость улетал на форму входа, вошедший получал «Ошибка 404».
 * Данные при этом живы, `GET /api/v1/public/reports/{slug}` отдаёт 200. То есть
 * клиент отправлял ссылку боссу, а тот упирался в чужую форму входа.
 *
 * Маршрут публичный: сессия ему не нужна и не мешает.
 *
 * Числа берутся из `figures` — свободного объекта, который наполняет сборщик
 * отчётов. Экран не притворяется, что знает про него больше, чем знает: он
 * показывает известные ему величины, а остальное не выдумывает. Ключи, которые
 * сюда доезжают, ограничены белым списком на стороне API.
 *
 * ФАЙЛА ЗДЕСЬ НЕТ И НЕ БУДЕТ. Раньше страница показывала кнопку «Скачать файл
 * отчёта» по непустому `download_url`. Поле осталось в контракте, но API его
 * больше не заполняет ни при каком формате (`seeno-ru-api#98`): документ отчёта
 * печатает состав вопросов клиента, а ссылку пересылают кому угодно.
 */

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { Mark } from '../ui/Mark';
import { Empty, Skeleton } from '../ui/state';
import { api } from '../api/client';
import { ApiError } from '../api/problem';
import {
  NOT_MEASURED,
  coverageOf,
  dateProse,
  percent,
  position,
  score as scoreFmt,
  statusLadder,
} from '@shared/src/format';
import { reportKindTitle } from '../ui/labels';
import type { components } from '../api/schema';

type SharedReport = components['schemas']['SharedReport'];

type Load =
  | { kind: 'loading' }
  | { kind: 'ready'; report: SharedReport }
  | { kind: 'failed'; error: unknown };

/** Прочитать число из свободного `figures`, не притворяясь, что оно там есть. */
function numberAt(figures: Record<string, unknown>, key: string): number | null {
  const value = figures[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

export function SharedReportPage() {
  const { slug = '' } = useParams();
  const [load, setLoad] = useState<Load>({ kind: 'loading' });

  useEffect(() => {
    const abort = new AbortController();
    api
      .get<SharedReport>(`/public/reports/${encodeURIComponent(slug)}`, {
        signal: abort.signal,
        quiet: true,
      })
      .then((report) => setLoad({ kind: 'ready', report }))
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        setLoad({ kind: 'failed', error });
      });
    return () => abort.abort();
  }, [slug]);

  return (
    <main className="auth">
      <div className="auth-card audit-card">
        <a className="brand" href="https://seeno.ru/">
          <Mark />
          seeno
        </a>
        {load.kind === 'loading' ? <Skeleton rows={4} /> : null}
        {load.kind === 'failed' ? <Missing error={load.error} /> : null}
        {load.kind === 'ready' ? <Figures report={load.report} /> : null}
      </div>
    </main>
  );
}

function Missing({ error }: { error: unknown }) {
  const gone = error instanceof ApiError && error.code === 'gone';
  return (
    <Empty
      eyebrow={gone ? 'Срок истёк' : 'Отчёт не найден'}
      title={gone ? 'Ссылка больше не действует' : 'Такого отчёта нет'}
      action={
        <Link className="btn btn-ghost" to="/auth/login">
          Войти в кабинет
        </Link>
      }
    >
      {gone
        ? 'У публичной ссылки есть срок. Попросите новую у того, кто её прислал.'
        : 'Возможно, адрес набран с ошибкой или ссылку отозвали.'}
    </Empty>
  );
}

function Figures({ report }: { report: SharedReport }) {
  const figures = report.figures as Record<string, unknown>;
  const index = numberAt(figures, 'index_score');
  const visibility = numberAt(figures, 'visibility');
  const avgPosition = numberAt(figures, 'avg_position');
  const topThree = numberAt(figures, 'top_three_rate');
  const parsedShare = numberAt(figures, 'position_parsed_share');
  const positioned = numberAt(figures, 'positioned');
  const mentions = numberAt(figures, 'organic_mentions');

  return (
    <>
      <h1>{report.branding?.organization_name ?? report.brand_name ?? 'Отчёт Seeno'}</h1>
      <p className="stamp">
        {reportKindTitle(report.kind)} · {dateProse(report.period_start)} —{' '}
        {dateProse(report.period_end)}
      </p>

      {index === null ? null : (
        <div className="audit-score">
          <div className="value tnum">{scoreFmt(index)}</div>
          <div className="audit-status">{statusLadder(index)}</div>
        </div>
      )}

      <table className="mini">
        <tbody>
          {/* Видимость печатается ОДНОЙ строкой. «Доля голоса» — это она же
              (§8 спеки), и `share_of_voice` в `figures` всегда равен
              `visibility` по построению. Две строки с одним числом под разными
              подписями читаются как два показателя, которые почему-то никогда
              не расходятся, — и кабинет рядом объясняет клиенту, что это одно
              и то же. Ключ из `figures` при этом никуда не делся: по нему живут
              уже разосланные ссылки. */}
          <tr>
            <td>Видимость</td>
            <td className="tnum">
              {visibility === null ? NOT_MEASURED : percent(visibility, 1)}
            </td>
          </tr>
          <tr>
            <td>В топ-3</td>
            <td className="tnum">
              {topThree === null ? NOT_MEASURED : percent(topThree, 1)}
            </td>
          </tr>
          <tr>
            <td>Средняя позиция</td>
            {/* Через общий форматировщик, а не голым числом. React печатает
                `number` через `toString()` — всегда с ТОЧКОЙ, — и публичный
                отчёт показывал «2.35» там, где кабинет показывал «2,4». */}
            <td className="tnum">
              {avgPosition === null ? NOT_MEASURED : position(avgPosition)}
            </td>
          </tr>
        </tbody>
      </table>

      {/* Основание доли «в топ-3» стоит под ней же: доля, посчитанная по одному
          разобранному месту из шести, без этой строки говорит о качестве нашего
          разбора, а читается как выдача движка. */}
      {parsedShare === null || mentions === null ? null : (
        <p className="stamp">
          Место в ответе разобрано у{' '}
          {coverageOf(
            positioned ?? 0,
            mentions,
            'упоминание',
            'упоминания',
            'упоминаний',
          )}{' '}
          ({percent(parsedShare, 1)}). Доли по позиции считаются только по ним.
        </p>
      )}

      {/* Файла по публичной ссылке нет ни у одного формата: документ отчёта
          несёт состав вопросов клиента целиком, а держатель ссылки — это кто
          угодно, кому её переслали. Кнопки нет не потому, что поле пустое, а
          потому что файла тут не будет никогда, и сказать об этом словами
          дешевле, чем оставить человека гадать, куда она делась. */}
      <p className="auth-fine">
        По ссылке видны числа за период. Сам файл отчёта скачивает владелец в своём
        кабинете.
      </p>

      {report.expires_at ? (
        <p className="auth-fine">
          Ссылка действует до {dateProse(report.expires_at)}. После этого страница
          перестанет открываться — это не ошибка.
        </p>
      ) : null}
    </>
  );
}
